import { Alert, Box, Button, Card, CardContent, Chip, LinearProgress, MenuItem, Select, Stack, TextField, Typography } from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { ApiLojaGet, ApiLojaInstance } from "data/ApiLoja";
import { getUserToken } from "utils/getToken";
import { certificateStatusLabel, participationStatusLabel } from "./attendanceLabels";
import { buildAttendanceExportRows } from "./attendanceExport";
import { requestCertificateAction } from "./certificateAdminApi";

type Session = { id: string; title: string; methods?: string[]; qrCheckInUrl?: string | null };
type AttendanceRegistration = { id: number; name: string; email: string; phone?: string; document: string; participationStatus: string; attendances: Array<{ session_id: string; status: string }> };
type AttendanceData = { sessions: Session[]; totals: { registrations: number; present: number }; registrations: AttendanceRegistration[] };
type ContentParticipant = { registrationId: number; progress: { percentage: number; completedItems: number; requiredItems: number } };
type ContentData = { participantsWithAccess: number; averageProgress: number; participants: ContentParticipant[] };
type CertificateParticipant = { id: number; certificateStatus: string; eligible: boolean; pending: string[] };

export const AttendanceManagement = ({ eventId }: { eventId: number }) => {
  const [attendance, setAttendance] = useState<AttendanceData>();
  const [content, setContent] = useState<ContentData>();
  const [certificates, setCertificates] = useState<CertificateParticipant[]>([]);
  const [sessionId, setSessionId] = useState(""); const [search, setSearch] = useState("");
  const [error, setError] = useState("");

  const load = async () => {
    try {
      setError("");
      const attendanceRequest = ApiLojaGet<AttendanceData>(`/admin/events/${eventId}/attendance`).then((response) => {
        setAttendance(response.data);
        setSessionId((old) => response.data.sessions.some((session) => session.id === old)
          ? old
          : response.data.sessions[0]?.id || "");
      });
      const contentRequest = ApiLojaGet<ContentData>(`/admin/events/${eventId}/content-progress`).then((response) => {
        setContent(response.data);
      });
      const certificateRequest = ApiLojaGet<{ registrations: CertificateParticipant[] }>(`/admin/events/${eventId}/certificates`).then((response) => {
        setCertificates(response.data.registrations);
      });

      await Promise.all([attendanceRequest, contentRequest, certificateRequest]);
    } catch (requestError: any) {
      const status = requestError?.response?.status;
      setError(requestError?.response?.data?.message || `Não foi possível carregar os participantes${status ? ` (HTTP ${status})` : ""}.`);
    }
  };
  useEffect(() => { load(); }, [eventId]);
  useEffect(() => {
    const reloadAfterSave = (event: Event) => {
      const savedEventId = (event as CustomEvent<{ eventId?: number }>).detail?.eventId;
      if (savedEventId === eventId) void load();
    };
    window.addEventListener("event-attendance-saved", reloadAfterSave);
    return () => window.removeEventListener("event-attendance-saved", reloadAfterSave);
  }, [eventId]);

  const changeAttendance = async (registrationId: number, status: string) => {
    const reason = window.prompt(status === "REVOKED" ? "Motivo obrigatório da revogação:" : "Motivo da alteração:") || "";
    if (status === "REVOKED" && !reason) return;
    try { await ApiLojaInstance.patch(`/admin/event-registrations/${registrationId}/attendance/${sessionId}`, { status, method: "MANUAL", reason }, { headers: { Authorization: getUserToken() } }); await load(); }
    catch (requestError: any) { setError(requestError?.response?.data?.message || "Não foi possível alterar a presença."); }
  };
  const changeCertificate = async (registrationId: number, action: string) => {
    const needsReason = ["REVOKE", "REISSUE", "CORRECT_NAME"].includes(action); const reason = needsReason ? window.prompt("Motivo obrigatório:") || "" : "";
    if (needsReason && !reason) return; let participantName;
    if (action === "CORRECT_NAME") { participantName = window.prompt("Nome oficial corrigido:") || ""; if (!participantName) return; }
    try { await requestCertificateAction(registrationId, action, reason, participantName); await load(); }
    catch (requestError: any) { setError(requestError?.response?.data?.message || "Não foi possível alterar o certificado."); }
  };
  const participants = useMemo(() => (attendance?.registrations || []).map((participant) => ({ ...participant, content: content?.participants.find((item) => Number(item.registrationId) === Number(participant.id)), certificate: certificates.find((item) => Number(item.id) === Number(participant.id)) })), [attendance, content, certificates]);
  if (error && !attendance) return <Alert severity="warning">{error}</Alert>;
  if (!attendance) return <Typography>Carregando participantes...</Typography>;
  const selected = attendance.sessions.find((item) => item.id === sessionId);
  const filtered = participants.filter((item) =>
    `${item.name} ${item.email} ${item.phone || ""} ${item.document}`
      .toLowerCase()
      .includes(search.toLowerCase())
  );
  const exportRegistrations = () => {
    const worksheet = XLSX.utils.json_to_sheet(
      buildAttendanceExportRows(participants, attendance.sessions)
    );
    worksheet["!cols"] = [
      { wch: 12 }, { wch: 34 }, { wch: 20 }, { wch: 20 }, { wch: 34 },
      { wch: 20 }, { wch: 23 }, { wch: 23 }, { wch: 24 }, { wch: 20 },
      ...attendance.sessions.map(() => ({ wch: 28 })),
    ];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Inscritos");
    XLSX.writeFile(workbook, `inscritos-evento-${eventId}.xlsx`);
  };

  return <Card sx={{ mt: 2 }}><CardContent><Stack gap={2}>
    <Stack direction={{ xs: "column", sm: "row" }} gap={1.5} justifyContent="space-between" alignItems={{ sm: "center" }}>
      <Typography variant="h5">Participantes</Typography>
      <Button
        variant="contained"
        color="info"
        startIcon={<FileDownloadOutlinedIcon />}
        onClick={exportRegistrations}
        disabled={!participants.length}
        sx={{
          color: "#ffffff !important",
          WebkitTextFillColor: "#ffffff !important",
          backgroundColor: "#155fc0 !important",
          border: "1px solid #155fc0 !important",
          opacity: "1 !important",
          "&:hover": {
            color: "#ffffff !important",
            backgroundColor: "#104b99 !important",
            borderColor: "#104b99 !important",
          },
          "& .MuiButton-startIcon, & .MuiSvgIcon-root": {
            color: "#ffffff !important",
            fill: "#ffffff !important",
            opacity: "1 !important",
          },
          "&.Mui-disabled, &.Mui-disabled:hover": {
            color: "#ffffff !important",
            WebkitTextFillColor: "#ffffff !important",
            borderColor: "#475569 !important",
            backgroundColor: "#64748b !important",
            opacity: "1 !important",
          },
          "&.Mui-disabled .MuiButton-startIcon, &.Mui-disabled .MuiSvgIcon-root": {
            color: "#ffffff !important",
            fill: "#ffffff !important",
            opacity: "1 !important",
          },
        }}
      >
        Exportar inscritos (.xlsx)
      </Button>
    </Stack>
    {error && <Alert severity="warning">{error}</Alert>}
    <Stack direction={{ xs: "column", md: "row" }} gap={2} flexWrap="wrap"><Typography>{attendance.totals.registrations} inscritos</Typography><Typography>{attendance.totals.present} presentes</Typography><Typography>{content?.participantsWithAccess || 0} acessaram o conteúdo</Typography><Typography>Progresso médio: {content?.averageProgress || 0}%</Typography></Stack>
    {!!attendance.sessions.length && (
      <Stack gap={2}>
        <Select
          fullWidth
          value={sessionId}
          onChange={(event) => setSessionId(event.target.value)}
        >
          {attendance.sessions.map((session) => (
            <MenuItem value={session.id} key={session.id}>{session.title}</MenuItem>
          ))}
        </Select>
      </Stack>
    )}
    <TextField label="Pesquisar participante" value={search} onChange={(event) => setSearch(event.target.value)} />
    {filtered.map((participant) => { const present = !!selected && participant.attendances.some((item) => item.session_id === sessionId && item.status === "PRESENT"); const certificate = participant.certificate; const progress = participant.content?.progress; return <Box key={participant.id} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 2 }}><Stack gap={1.5}>
      <Stack direction={{ xs: "column", md: "row" }} gap={1} alignItems={{ md: "center" }}><Box flex={1}><Typography fontWeight="bold">{participant.name}</Typography><Typography variant="body2">{participant.document} · {participant.phone || "Telefone não informado"} · {participant.email}</Typography></Box><Chip label={`Participação: ${participationStatusLabel(participant.participationStatus)}`} /><Chip label={selected ? (present ? "Presente" : "Ausente") : "Sem sessão de presença"} color={present ? "success" : "default"} />{certificate && <Chip label={`Certificado: ${certificateStatusLabel(certificate.certificateStatus)}`} />}</Stack>
      <Box><Stack direction="row" justifyContent="space-between"><Typography variant="body2">Conteúdo</Typography><Typography variant="body2">{progress?.percentage || 0}% ({progress?.completedItems || 0}/{progress?.requiredItems || 0})</Typography></Stack><LinearProgress variant="determinate" value={progress?.percentage || 0} /></Box>
      {certificate?.pending?.length ? <Typography variant="caption">Pendências do certificado: {certificate.pending.join(" ")}</Typography> : null}
      <Stack direction="row" gap={1} flexWrap="wrap">{selected && <Button onClick={() => changeAttendance(participant.id, present ? "REVOKED" : "PRESENT")}>{present ? "Revogar presença" : "Marcar presença"}</Button>}{certificate && <>{["AVAILABLE", "ISSUED"].includes(certificate.certificateStatus) && <Button onClick={() => changeCertificate(participant.id, "BLOCK")}>Bloquear</Button>}{certificate.certificateStatus === "BLOCKED" && <Button onClick={() => changeCertificate(participant.id, "UNBLOCK")}>Desbloquear</Button>}<Button onClick={() => changeCertificate(participant.id, "CORRECT_NAME")}>Corrigir nome</Button><Button onClick={() => changeCertificate(participant.id, "REISSUE")}>Reemitir</Button>{certificate.certificateStatus !== "REVOKED" && <Button color="error" onClick={() => changeCertificate(participant.id, "REVOKE")}>Revogar certificado</Button>}</>}</Stack>
    </Stack></Box>; })}
  </Stack></CardContent></Card>;
};
